import { useEffect, useLayoutEffect } from 'react';

import { Sentry } from '@opendoor/observability/sentry';
import { debugLog } from 'platform/debugger';
import { globalTrackError, globalTrackEvent, useObservability } from 'platform/observability';

// Module-load mark so `useTrackTimeToRender` always has a `cake-initial-load-event`
// entry to anchor its measure against, even when the tracked component's
// `useLayoutEffect` fires in the same commit as `AppContents` (i.e. before
// `useTrackInitialLoad`'s `useEffect` would otherwise create the mark).
// `useTrackInitialLoad` later clears and re-marks; both branches stay supported
// because `useTrackTimeToRender` reads the *last* mark with this name.
if (typeof window !== 'undefined') {
  performance.mark('cake-initial-load-event');
}

/** @internal */
export type TrackedExperience =
  | 'prelimOffer-offerCard' // seller prelim offer card
  | 'finalOffer-offerCard' // seller final offer card
  | 'closingOffer-offerCard'; // seller closing offer card

/**
 * WARNING!!!!:
 * DO NOT use this hook INSIDE a modal
 * DO NOT use this hook on a page where a modal typically pops opens on load (this is for modals where people typically spend a lot of time on)
 *
 * Why:
 * - We track the time to render from the last route change or navigation start.
 * - Modals make things tricky as we do not consider a modal opening or closing as a route change. In the future, we will consider tracking this as a start event when we have a provider than handles all modals.
 *
 * Now the info:
 * Allows us to track the time it takes for a component to render from navigation state or the route change event - measured in milliseconds
 * Uses the performance API to:
 * 1. Create a performance mark after rendering
 * 2. Measure the time between navigation start or the last route change mark and the latest render mark with performance.measure
 * 3. Track the time to render as an observability trackEvent. This also passes in if this is from an 'initialLoad' or 'routeChange' event to help us understand the context of the time to render.
 *
 * Event: time-to-render
 * Label: `productExperience-componentName` example: prelimOffer-offerCard
 * @param trackedExperience - tracker name should be made up of the product experience the component is shown on and the name of the component that we're tracking performance on
 *
 * The combination of the team, productExperience, and componentName should be unique to the experience shown
 * For example: if the component is shown on two different experiences but on the same path you can use an if/else statement to ensure you're uniquely tracking in this hook (just updated the productExperience to be more unique to what you want to track)
 */
export const useTrackTimeToRender = (
  trackedExperience: TrackedExperience,
  /**
   * Allows people to opt out of tracking if needed
   * Helpful for opting out in cases where the component that is being tracked lives in a modal or have long-standing modals that open on page load (see warning for more details)
   */
  optOutOfTracking?: boolean,
) => {
  const { trackEvent, trackError } = useObservability();

  // Ideally we'd track the moment that the pixels are rendered on the screen,
  // but that's a hard problem because of how unpredictable React's scheduler is
  // in when it executes hooks. Here, we're using useLayoutEffect(), which is
  // always executed AFTER render but BEFORE paint as an approximation.
  //
  // This provides more consistency than useEffect (see https://jser.dev/2023-08-09-effects-run-paint)
  // and doesn't require the tab to be foregrounded to get an accurate measurement
  // like some other approaches (e.g. https://webperf.tips/tip/react-hook-paint/).
  useLayoutEffect(() => {
    try {
      if (optOutOfTracking) {
        return;
      }

      const analyticsName = `${trackedExperience}`;
      const markAnalyticsName = `${analyticsName}-end`;

      // Gets the performance entries for the initial load and route change events
      const initialLoadEntries = performance.getEntriesByName('cake-initial-load-event') ?? [];
      const routeChangeEntries = performance.getEntriesByName('cake-route-change-event') ?? [];

      // Determine which view load type event happened last (initialLoad or routeChange)
      const lastInitialLoadEntry = initialLoadEntries[initialLoadEntries.length - 1];
      const lastRouteChangeEntry = routeChangeEntries[routeChangeEntries.length - 1];
      const latestViewLoadEntry =
        lastRouteChangeEntry?.startTime > lastInitialLoadEntry?.startTime
          ? lastRouteChangeEntry
          : lastInitialLoadEntry;

      // Don't re-track if no navigation/load has happened since the last render
      const ttuiEntries = performance.getEntriesByName(markAnalyticsName) ?? [];
      const lastTTUIEntry = ttuiEntries[ttuiEntries.length - 1];

      /**
       * If the component as re-rendered for ANY reason other than a full page load caused by a route change or initial load event, we should not track the time to render. We only care about the TTUI relative to
       */
      if (lastTTUIEntry?.startTime > latestViewLoadEntry?.startTime) {
        return;
      }

      // Marks when this specific component mounts
      performance.mark(markAnalyticsName);

      // https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#how-loading-time-is-calculated
      if (latestViewLoadEntry) {
        let startTime;
        let viewLoadType: 'initialLoad' | 'routeChange';
        if (latestViewLoadEntry === lastRouteChangeEntry) {
          startTime = latestViewLoadEntry.startTime;
          viewLoadType = 'routeChange';
        } else {
          /**
           * It's only meaningful to measure the time from navigation start if it's the initial load
           * This ensures TTUI for initial load also captures the server's response time when fetching
           * the document, nor how long it takes to download, parse, and execute the initial Javascript
           * bundles required to mount the React root.
           */
          startTime = 0;
          viewLoadType = 'initialLoad';
        }

        // https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure
        const timeToRender = performance.measure(`${analyticsName}-render`, {
          start: startTime,
          end: markAnalyticsName,
        });

        let percentOfTimeTabVisible;
        let percentOfTimeTabVisibleDebugLine = '';
        if (viewLoadType === 'initialLoad') {
          percentOfTimeTabVisible =
            window?.['_opendoor']?.tabVisibilityTracker?.getPercentOfTimeTabVisible();

          if (typeof percentOfTimeTabVisible === 'number') {
            percentOfTimeTabVisibleDebugLine = ` (visible ${percentOfTimeTabVisible}% of the time)`;
          } else {
            Sentry.captureMessage(
              `Received unexpected value from getPercentOfTimeTabVisible(): ${percentOfTimeTabVisible}`,
            );
          }
        }

        trackEvent(
          'time-to-render',
          analyticsName,
          {
            renderStartTime: timeToRender.startTime,
            timeToRender: timeToRender.duration,
            viewLoadType,
            // Browsers sometimes pause or throttle Javascript execution when
            // tabs are backgrounded, which could slow down time to render
            percentOfTimeTabVisible,
          },
          {
            addTiming: true,
          },
        );

        debugLog(
          `[TTUI:${viewLoadType}] ${analyticsName}: ${Math.round(
            timeToRender.duration,
          )}ms starting at ${Math.round(
            timeToRender.startTime,
          )}ms${percentOfTimeTabVisibleDebugLine}`,
        );
      } else {
        // eslint-disable-next-line @opendoor/track-error-prefer-throw -- Performance measurement gaps are telemetry-only and should not block rendering.
        trackError(
          'time-to-render-error',
          new Error(`Performance initial load or navigation entry not found for ${analyticsName}`),
        );
      }
    } catch (error) {
      // https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure#exceptions
      globalTrackError(
        'time-to-render-error',
        new Error(`Error tracking time to render for ${trackedExperience}`),
        { error },
      );
    }

    // We'll only execute this when the component is mounted. If the component
    // isn't unmounted between route navigations, then this event doesn't make
    // sense. Dynamically changing `optOutOfTracking` or `trackedExperience` is
    // not supported because it would cause inaccurate measurements.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See explanation above
  }, []);
};

/**
 * Creates a performance.mark on initial load
 * This allows us to accurately track time to render for components - since we use this as the start time for the time to render.
 * A performance mark is a high resolution timestamp marker in the browser's performance timeline.
 * https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark
 */
export const useTrackInitialLoad = () => {
  const { trackEvent } = useObservability();
  useEffect(() => {
    performance.clearMarks('cake-initial-load-event');
    const initialLoadMark = performance.mark('cake-initial-load-event');
    trackEvent(
      'load-event',
      'cake-initial-load-event',
      {
        markedStartTime: initialLoadMark.startTime,
        duration: initialLoadMark.duration,
      },
      {
        addTiming: true,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only track initial load
  }, []);
};

/**
 * Creates a performance.mark on the route change event
 * This allows us to accurately track time to render for components - since we use this as the start time for the time to render.
 * A performance mark is a high resolution timestamp marker in the browser's performance timeline.
 * https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark
 */
export const trackNavigationEvent = () => {
  performance.clearMarks('cake-route-change-event');
  const navigationMark = performance.mark('cake-route-change-event');
  globalTrackEvent(
    'navigation-event',
    'cake-route-change-event',
    {
      data: {
        renderStartTime: navigationMark.startTime,
        duration: navigationMark.duration,
      },
    },
    {
      addTiming: true,
    },
  );
};
